/**
 * Pagina de detalhe/edicao de perfil de acesso. Rota /dashboard/perfis/:id
 * (id = "novo" pra criacao). Segue o mesmo padrao de ClientDetailPage /
 * UserDetailPage.
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, useToast } from "@payflow/ui";
import { RoleForm, useAvailablePermissions } from "./RolesPage";
import {
  createRole,
  updateRole,
  getRole,
  type RoleResponse,
  type CreateRoleData,
  type UpdateRoleData,
} from "../../services/roles.service";

export function RoleDetailPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "novo";
  const availablePermissions = useAvailablePermissions();

  const [role, setRole] = useState<RoleResponse | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    setLoading(true);
    getRole(id!)
      .then((r) => {
        if (!cancelled) setRole(r);
      })
      .catch(() => {
        if (!cancelled) {
          toast({ variant: "error", title: t("roles.load_error") });
          navigate("/dashboard/perfis", { replace: true });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, navigate, t, toast]);

  const handleSave = async (data: CreateRoleData | UpdateRoleData) => {
    setSaving(true);
    try {
      if (role) {
        await updateRole(role.id, data as UpdateRoleData);
        toast({ variant: "success", title: t("roles.update_success") });
      } else {
        await createRole(data as CreateRoleData);
        toast({ variant: "success", title: t("roles.create_success") });
      }
      navigate("/dashboard/perfis");
    } catch (err) {
      toast({
        variant: "error",
        title: err instanceof Error ? err.message : t("roles.save_error"),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-8rem)] flex-col gap-4">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/dashboard/perfis")}
          className="h-9 w-9 p-0"
          aria-label={t("common.back")}
        >
          <BackIcon />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {role ? t("roles.edit_title") : t("roles.new_title")}
          </h1>
          {role && <p className="text-sm text-muted-foreground">{role.nome}</p>}
        </div>
      </div>

      <div className="flex flex-1 flex-col rounded-lg border border-border bg-card px-6 py-4">
        {loading ? (
          <div className="flex flex-1 items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
          </div>
        ) : (
          <RoleForm
            variant="page"
            role={role}
            saving={saving}
            onSave={handleSave}
            availablePermissions={availablePermissions}
          />
        )}
      </div>
    </div>
  );
}

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
